import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { ShieldQuestion } from "lucide-react"
import { useCallback, useRef } from "react"
import type { JsonValue } from "../../nest/generated"
import { Modal } from "../ui"

export type BrowserControlPromptResponse = { decision: "allow" | "deny" }

export interface BrowserControlPromptPage {
    title: string
    url: string
}

export interface BrowserControlPromptTarget {
    tabId: string
    tag: string
    role: string
    type: string
    name: string
}

export interface BrowserControlPromptModalProps {
    toolName: string
    pages: BrowserControlPromptPage[]
    targets: BrowserControlPromptTarget[]
    input: JsonValue
}

export function BrowserControlPromptDialog({
    toolName,
    pages,
    targets,
    input,
    onDecision,
}: BrowserControlPromptModalProps & {
    onDecision: (response: BrowserControlPromptResponse) => void
}): JSX.Element {
    return (
        <Modal title="Allow Browser action?" onClose={() => onDecision({ decision: "deny" })} hideSeparator>
            <div className="flex flex-col gap-4 text-sm text-base-content">
                <div className="flex items-start gap-3 border border-primary/30 bg-primary/5 p-3">
                    <ShieldQuestion size={18} className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                        <p className="font-medium">The agent wants to control {pages.length === 1 ? "this page" : "these pages"}</p>
                        {pages.map((page, index) => (
                            <div key={`${page.url}:${index}`} className="mt-1">
                                <p className="break-words text-xs leading-relaxed text-muted">{page.title || "Untitled page"}</p>
                                <p className="break-all text-[11px] leading-relaxed text-muted">{page.url}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border border-border bg-base-200/40 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted">Requested control</p>
                    <p className="mt-1 break-all font-mono text-xs">{toolName}</p>
                    {targets.length > 0 && (
                        <div className="mt-2 border border-border bg-base-100 p-2 text-xs">
                            <p className="font-medium">Target {targets.length === 1 ? "control" : "controls"}</p>
                            {targets.map((target, index) => (
                                <p key={`${target.tabId}:${target.name}:${index}`} className="mt-1 break-words text-muted">
                                    {target.name || "Unnamed control"} · {target.role || target.tag}
                                    {target.type ? ` · ${target.type}` : ""}
                                </p>
                            ))}
                        </div>
                    )}
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words border border-border bg-base-100 p-2 text-[11px] leading-relaxed">
                        {JSON.stringify(input, null, 2)}
                    </pre>
                </div>

                <p className="text-xs leading-relaxed text-muted">
                    This approval applies once to the exact control above. Review it carefully: allowing it may authorize a send, submission, purchase,
                    publication, access change, or another consequential external effect.
                </p>

                <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                        type="button"
                        autoFocus
                        className="btn min-h-10 flex-1 border border-border bg-base-200 px-4 py-2.5 font-medium text-base-content hover:bg-base-300"
                        onClick={() => onDecision({ decision: "deny" })}
                    >
                        Deny
                    </button>
                    <button
                        type="button"
                        className="btn min-h-10 flex-1 bg-primary px-4 py-2.5 font-medium text-primary-content hover:bg-primary/90"
                        onClick={() => onDecision({ decision: "allow" })}
                    >
                        Allow once
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export const BrowserControlPromptModal = NiceModal.create((props: BrowserControlPromptModalProps) => {
    const modal = useModal()
    const settled = useRef(false)
    const settle = useCallback(
        (response: BrowserControlPromptResponse) => {
            if (settled.current) return
            settled.current = true
            modal.resolve(response)
            modal.remove()
        },
        [modal]
    )
    return <BrowserControlPromptDialog {...props} onDecision={settle} />
})

let controlPromptQueue: Promise<void> = Promise.resolve()

export function showBrowserControlPrompt(props: BrowserControlPromptModalProps, signal: AbortSignal): Promise<BrowserControlPromptResponse> {
    const prompt = controlPromptQueue.then(async (): Promise<BrowserControlPromptResponse> => {
        if (signal.aborted) throw new DOMException("Browser control was cancelled", "AbortError")
        const response = await new Promise<unknown>((resolve, reject) => {
            const abort = () => {
                void NiceModal.remove(BrowserControlPromptModal)
                reject(new DOMException("Browser control was cancelled", "AbortError"))
            }
            signal.addEventListener("abort", abort, { once: true })
            void NiceModal.show(BrowserControlPromptModal, props)
                .then(resolve, reject)
                .finally(() => signal.removeEventListener("abort", abort))
        })
        if (typeof response !== "object" || response === null || !("decision" in response) || response.decision !== "allow") {
            return { decision: "deny" }
        }
        return { decision: "allow" }
    })
    controlPromptQueue = prompt.then(
        () => undefined,
        () => undefined
    )
    return prompt
}
